'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/local-api/client';
import { blogsApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { PencilSquareIcon, HeartIcon, ChatBubbleLeftIcon, BookmarkIcon, ClockIcon, MagnifyingGlassIcon, FireIcon, SparklesIcon, ArrowRightIcon, ChevronLeftIcon, ChevronRightIcon, TrophyIcon, UserPlusIcon, UserMinusIcon, RssIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid, BookmarkIcon as BookmarkSolid } from '@heroicons/react/24/solid';

interface Blog {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  tags: string[];
  read_time_mins: number;
  likes_count: number;
  comments_count: number;
  bookmarks_count: number;
  views_count: number;
  published_at: string | null;
  created_at: string;
  author_id: string;
  user_profiles?: { full_name: string; avatar_url?: string | null; username?: string | null };
}

const TAG_COLORS: Record<string, string> = {
  Crypto: 'bg-amber-500/15 text-amber-400 border-amber-500/20',
  Safety: 'bg-red-500/15 text-red-400 border-red-500/20',
  DeFi: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/20',
  Forex: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
  Education: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
  Trading: 'bg-green-500/15 text-green-400 border-green-500/20',
  Research: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  Earning: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  Warning: 'bg-rose-500/15 text-rose-400 border-rose-500/20',
  Community: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
};

const ALL_TAGS = ['All', 'Crypto', 'DeFi', 'Forex', 'Trading', 'Education', 'Safety', 'Research', 'Earning'];

const RANK_STYLES = [
  'from-amber-500 to-yellow-400',
  'from-slate-400 to-slate-300',
  'from-amber-700 to-amber-600',
  'from-primary/70 to-primary/50',
  'from-primary/50 to-primary/30',
];

type FeedTab = 'all' | 'trending' | 'recommended' | 'following';

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function AuthorAvatar({ name, avatarUrl, size = 8 }: { name: string; avatarUrl?: string | null; size?: number }) {
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className={`w-${size} h-${size} rounded-full object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div className={`w-${size} h-${size} rounded-full bg-gradient-to-br from-primary/40 to-primary/20 flex items-center justify-center flex-shrink-0`}>
      <span className="text-[10px] font-bold text-primary">{initials}</span>
    </div>
  );
}

// ─── Trending This Week ───────────────────────────────────────────────────────
function TrendingSection({ blogs }: { blogs: Blog[] }) {
  if (blogs.length === 0) return null;
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-lg bg-amber-500/15 flex items-center justify-center">
          <TrophyIcon className="text-amber-400" style={{ width: 15, height: 15 }} />
        </div>
        <h2 className="text-base font-bold text-foreground">Trending This Week</h2>
        <span className="ml-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 text-[10px] font-bold border border-amber-500/20">
          🔥 Top by Likes
        </span>
      </div>
      <div className="rounded-2xl border border-border/40 bg-card overflow-hidden divide-y divide-border/30">
        {blogs.map((blog, idx) => (
          <Link key={blog.id} href={`/blogs/${blog.slug}`} className="group flex items-center gap-4 px-4 py-3.5 hover:bg-muted/30 transition-colors">
            <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${RANK_STYLES[idx]} flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <span className="text-[11px] font-black text-white">#{idx + 1}</span>
            </div>
            {blog.cover_image_url ? (
              <img src={blog.cover_image_url} alt={blog.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-border/30" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 border border-border/30">
                <PencilSquareIcon className="text-muted-foreground" style={{ width: 18, height: 18 }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-1 leading-snug">
                {blog.title}
              </h3>
              <div className="flex items-center gap-2.5 mt-1">
                <span className="text-[10px] text-muted-foreground">{(blog.user_profiles as any)?.full_name || 'Member'}</span>
                <span className="text-[10px] text-muted-foreground/50">·</span>
                <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                  <ClockIcon style={{ width: 10, height: 10 }} /> {blog.read_time_mins}m
                </span>
                {(blog.tags || []).slice(0, 1).map(tag => (
                  <span key={tag} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${TAG_COLORS[tag] || 'bg-muted text-muted-foreground border-border'}`}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1 text-red-400 flex-shrink-0">
              <HeartSolid style={{ width: 13, height: 13 }} />
              <span className="text-xs font-bold">{blog.likes_count}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Recommended For You Carousel ────────────────────────────────────────────
function RecommendedCarousel({ blogs }: { blogs: Blog[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();
    return () => el.removeEventListener('scroll', checkScroll);
  }, [blogs]);

  if (blogs.length === 0) return null;

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/15 flex items-center justify-center">
            <SparklesIcon className="text-primary" style={{ width: 15, height: 15 }} />
          </div>
          <h2 className="text-base font-bold text-foreground">Recommended for You</h2>
          <span className="ml-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold border border-primary/20">
            Based on your interests
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all ${canScrollLeft ? 'border-border/60 text-foreground hover:bg-muted' : 'border-border/20 text-muted-foreground/30 cursor-not-allowed'}`}
          >
            <ChevronLeftIcon style={{ width: 14, height: 14 }} />
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`w-7 h-7 rounded-full border flex items-center justify-center transition-all ${canScrollRight ? 'border-border/60 text-foreground hover:bg-muted' : 'border-border/20 text-muted-foreground/30 cursor-not-allowed'}`}
          >
            <ChevronRightIcon style={{ width: 14, height: 14 }} />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-2 scrollbar-none"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {blogs.map(blog => (
          <Link
            key={blog.id}
            href={`/blogs/${blog.slug}`}
            className="group flex-shrink-0 w-64 rounded-2xl border border-border/40 bg-card overflow-hidden hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5 transition-all duration-300"
            style={{ scrollSnapAlign: 'start' }}
          >
            {blog.cover_image_url ? (
              <div className="h-32 overflow-hidden">
                <img src={blog.cover_image_url} alt={blog.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            ) : (
              <div className="h-32 bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center">
                <PencilSquareIcon className="text-primary/30" style={{ width: 32, height: 32 }} />
              </div>
            )}
            <div className="p-4">
              <div className="flex flex-wrap gap-1 mb-2">
                {(blog.tags || []).slice(0, 2).map(tag => (
                  <span key={tag} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${TAG_COLORS[tag] || 'bg-muted text-muted-foreground border-border'}`}>{tag}</span>
                ))}
              </div>
              <h3 className="text-sm font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug mb-2">
                {blog.title}
              </h3>
              <div className="flex items-center justify-between mt-auto">
                <div className="flex items-center gap-1.5">
                  <AuthorAvatar
                    name={(blog.user_profiles as any)?.full_name || 'Author'}
                    avatarUrl={(blog.user_profiles as any)?.avatar_url}
                    size={5}
                  />
                  <span className="text-[10px] text-muted-foreground truncate max-w-[80px]">{(blog.user_profiles as any)?.full_name || 'Member'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span className="flex items-center gap-0.5 text-[10px]">
                    <HeartIcon style={{ width: 10, height: 10 }} /> {blog.likes_count}
                  </span>
                  <span className="flex items-center gap-0.5 text-[10px]">
                    <ClockIcon style={{ width: 10, height: 10 }} /> {blog.read_time_mins}m
                  </span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Following Feed ───────────────────────────────────────────────────────────
interface FollowedAuthor {
  id: string;
  full_name: string;
  avatar_url?: string | null;
  username?: string | null;
}

interface FollowingFeedProps {
  blogs: Blog[];
  followedAuthors: FollowedAuthor[];
  followedIds: Set<string>;
  onUnfollow: (authorId: string) => void;
  loading: boolean;
  user: any;
  onLogin: () => void;
  likedIds: Set<string>;
  bookmarkedIds: Set<string>;
  onToggleLike: (blogId: string, e: React.MouseEvent) => void;
  onToggleBookmark: (blogId: string, e: React.MouseEvent) => void;
}

function FollowingFeed({
  blogs,
  followedAuthors,
  followedIds,
  onUnfollow,
  loading,
  user,
  onLogin,
  likedIds,
  bookmarkedIds,
  onToggleLike,
  onToggleBookmark,
}: FollowingFeedProps) {
  if (!user) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <RssIcon className="text-primary" style={{ width: 28, height: 28 }} />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-2">Your Following Feed</h3>
        <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
          Sign in to follow your favourite authors and see their latest posts here.
        </p>
        <button
          onClick={onLogin}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          Sign In to Follow Authors
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-2xl bg-card border border-border/40 p-5 animate-pulse">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-muted" />
              <div className="h-3 bg-muted rounded w-32" />
            </div>
            <div className="h-4 bg-muted rounded w-3/4 mb-2" />
            <div className="h-3 bg-muted rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (followedAuthors.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <UserPlusIcon className="text-primary" style={{ width: 28, height: 28 }} />
        </div>
        <h3 className="text-lg font-bold text-foreground mb-2">No authors followed yet</h3>
        <p className="text-muted-foreground text-sm max-w-sm mx-auto">
          Browse blogs and click <strong>Follow</strong> on any author to see their latest posts here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Followed authors strip */}
      <div className="mb-6">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Following ({followedAuthors.length})</p>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none">
          {followedAuthors.map(author => (
            <div key={author.id} className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <div className="relative">
                <AuthorAvatar name={author.full_name} avatarUrl={author.avatar_url} size={10} />
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-primary border-2 border-background" />
              </div>
              <span className="text-[10px] text-muted-foreground max-w-[56px] truncate text-center">{author.full_name.split(' ')[0]}</span>
            </div>
          ))}
        </div>
      </div>

      {blogs.length === 0 ? (
        <div className="text-center py-12 rounded-2xl border border-border/40 bg-card">
          <RssIcon className="text-muted-foreground mx-auto mb-3" style={{ width: 28, height: 28 }} />
          <p className="text-foreground font-semibold mb-1">No posts yet</p>
          <p className="text-muted-foreground text-sm">Authors you follow haven't published anything yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {blogs.map(blog => {
            const authorName = (blog.user_profiles as any)?.full_name || 'Member';
            const authorAvatar = (blog.user_profiles as any)?.avatar_url;
            return (
              <Link key={blog.id} href={`/blogs/${blog.slug}`} className="group block">
                <article className="rounded-2xl border border-border/40 bg-card overflow-hidden transition-all duration-300 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5">
                  <div className="flex gap-4 p-5">
                    {blog.cover_image_url && (
                      <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={blog.cover_image_url} alt={blog.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {/* Author row with follow/unfollow */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <AuthorAvatar name={authorName} avatarUrl={authorAvatar} size={6} />
                          <div>
                            <p className="text-[11px] font-semibold text-foreground leading-none">{authorName}</p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(blog.published_at || blog.created_at)}</p>
                          </div>
                        </div>
                        <button
                          onClick={e => { e.preventDefault(); onUnfollow(blog.author_id); }}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold border border-border/60 text-muted-foreground hover:border-red-400/50 hover:text-red-400 transition-all"
                        >
                          <UserMinusIcon style={{ width: 11, height: 11 }} /> Unfollow
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-1.5">
                        {(blog.tags || []).slice(0, 2).map(tag => (
                          <span key={tag} className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${TAG_COLORS[tag] || 'bg-muted text-muted-foreground border-border'}`}>{tag}</span>
                        ))}
                      </div>
                      <h3 className="text-sm font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 leading-snug mb-1">
                        {blog.title}
                      </h3>
                      {blog.excerpt && (
                        <p className="text-muted-foreground text-[11px] leading-relaxed line-clamp-1">{blog.excerpt}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2.5">
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <ClockIcon style={{ width: 11, height: 11 }} /> {blog.read_time_mins}m
                        </span>
                        <button
                          onClick={e => onToggleLike(blog.id, e)}
                          className={`flex items-center gap-1 text-[11px] transition-colors ${likedIds.has(blog.id) ? 'text-red-400' : 'text-muted-foreground hover:text-red-400'}`}
                        >
                          {likedIds.has(blog.id) ? <HeartSolid style={{ width: 12, height: 12 }} /> : <HeartIcon style={{ width: 12, height: 12 }} />}
                          {blog.likes_count + (likedIds.has(blog.id) ? 1 : 0)}
                        </button>
                        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <ChatBubbleLeftIcon style={{ width: 11, height: 11 }} /> {blog.comments_count}
                        </span>
                        <button
                          onClick={e => onToggleBookmark(blog.id, e)}
                          className={`transition-colors ${bookmarkedIds.has(blog.id) ? 'text-amber-400' : 'text-muted-foreground hover:text-amber-400'}`}
                        >
                          {bookmarkedIds.has(blog.id) ? <BookmarkSolid style={{ width: 12, height: 12 }} /> : <BookmarkIcon style={{ width: 12, height: 12 }} />}
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Reading history helpers ──────────────────────────────────────────────────
const HISTORY_KEY = 'blog_reading_history';
const HISTORY_LIMIT = 20;

function getReadingHistory(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch {
    return [];
  }
}

export function recordBlogView(blogId: string) {
  try {
    const history = getReadingHistory().filter(id => id !== blogId);
    history.unshift(blogId);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, HISTORY_LIMIT)));
  } catch {
    // ignore
  }
}

export default function BlogsContent() {
  const { user } = useAuth();
  const router = useRouter();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [loadError, setLoadError] = useState('');
  const [featured, setFeatured] = useState<Blog | null>(null);
  const [trendingBlogs, setTrendingBlogs] = useState<Blog[]>([]);
  const [recommendedBlogs, setRecommendedBlogs] = useState<Blog[]>([]);
  const [followingBlogs, setFollowingBlogs] = useState<Blog[]>([]);
  const [followedAuthors, setFollowedAuthors] = useState<FollowedAuthor[]>([]);
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [activeTag, setActiveTag] = useState('All');
  const [search, setSearch] = useState('');
  const [feedTab, setFeedTab] = useState<FeedTab>('all');
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());

  // Fetch trending
  const fetchTrending = useCallback(async () => {
    const { data } = await blogsApi.list({ tab: 'trending', limit: 5 });
    if (data) {
      const rows = Array.isArray(data) ? data : (data as any)?.data ?? [];
      setTrendingBlogs(rows as Blog[]);
    }
  }, []);

  // Fetch recommended
  const fetchRecommended = useCallback(async () => {
    const supabase = createClient();
    const history = getReadingHistory();
    let interestTags: string[] = [];

    if (history.length > 0) {
      const { data: readBlogs } = await supabase
        .from('blogs')
        .select('tags')
        .in('id', history.slice(0, 10));
      if (readBlogs) {
        const tagCounts: Record<string, number> = {};
        readBlogs.forEach((b: any) => {
          (b.tags || []).forEach((t: string) => {
            tagCounts[t] = (tagCounts[t] || 0) + 1;
          });
        });
        interestTags = Object.entries(tagCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([tag]) => tag);
      }
    }

    if (user && interestTags.length < 3) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('interests')
        .eq('id', user.id)
        .single();
      if (profile?.interests && Array.isArray(profile.interests)) {
        interestTags = [...new Set([...interestTags, ...profile.interests])].slice(0, 4);
      }
    }

    if (interestTags.length === 0) {
      interestTags = ['Crypto', 'Trading', 'DeFi'];
    }

    const { data } = await supabase
      .from('blogs')
      .select('id, title, slug, excerpt, cover_image_url, tags, read_time_mins, likes_count, comments_count, bookmarks_count, views_count, published_at, created_at, author_id, user_profiles(full_name, avatar_url, username)')
      .in('status', ['approved', 'published'])
      .overlaps('tags', interestTags)
      .order('likes_count', { ascending: false })
      .limit(10);

    const filtered = (data || []).filter((b: any) => !history.includes(b.id));
    setRecommendedBlogs((filtered.length > 0 ? filtered : (data || [])) as Blog[]);
  }, [user]);

  // Fetch following data
  const fetchFollowing = useCallback(async () => {
    if (!user) return;
    setFollowingLoading(true);
    const supabase = createClient();

    // Get followed author IDs
    const { data: follows } = await supabase
      .from('blog_author_follows')
      .select('author_id')
      .eq('follower_id', user.id);

    const authorIds = (follows || []).map((f: any) => f.author_id);
    setFollowedIds(new Set(authorIds));

    if (authorIds.length === 0) {
      setFollowedAuthors([]);
      setFollowingBlogs([]);
      setFollowingLoading(false);
      return;
    }

    // Get author profiles
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name, avatar_url, username')
      .in('id', authorIds);
    setFollowedAuthors((profiles || []) as FollowedAuthor[]);

    // Get latest blogs from followed authors
    const { data: fBlogs } = await supabase
      .from('blogs')
      .select('id, title, slug, excerpt, cover_image_url, tags, read_time_mins, likes_count, comments_count, bookmarks_count, views_count, published_at, created_at, author_id, user_profiles(full_name, avatar_url, username)')
      .eq('status', 'approved')
      .in('author_id', authorIds)
      .order('published_at', { ascending: false })
      .limit(30);

    setFollowingBlogs((fBlogs || []) as Blog[]);
    setFollowingLoading(false);
  }, [user]);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const { data, error } = await blogsApi.list({
      tag: activeTag !== 'All' ? activeTag : undefined,
      search: search.trim() || undefined,
      limit: 30,
    });
    if (error) setLoadError('Could not load blogs. Please try again.');
    if (data) {
      const rows = Array.isArray(data) ? data : (data as any)?.data ?? [];
      if (rows.length > 0) {
        setFeatured(rows[0] as Blog);
        setBlogs(rows.slice(1) as Blog[]);
      } else {
        setFeatured(null);
        setBlogs([]);
      }
    }
    setLoading(false);
  }, [activeTag, search]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);
  useEffect(() => { fetchTrending(); }, [fetchTrending]);
  useEffect(() => { fetchRecommended(); }, [fetchRecommended]);
  useEffect(() => { fetchFollowing(); }, [fetchFollowing]);

  useEffect(() => {
    if (!user) return;
    const fetchInteractions = async () => {
      const supabase = createClient();
      const [likesResult, bookmarksResult] = await Promise.all([
        supabase.from('blog_likes').select('blog_id').eq('user_id', user.id),
        supabase.from('blog_bookmarks').select('blog_id').eq('user_id', user.id),
      ]);
      setLikedIds(new Set((likesResult.data || []).map((l: any) => l.blog_id)));
      setBookmarkedIds(new Set((bookmarksResult.data || []).map((b: any) => b.blog_id)));
    };
    fetchInteractions();
  }, [user]);

  const toggleLike = async (blogId: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) { router.push('/login'); return; }
    const supabase = createClient();
    const isLiked = likedIds.has(blogId);
    if (isLiked) {
      await supabase.from('blog_likes').delete().eq('blog_id', blogId).eq('user_id', user.id);
      setLikedIds(prev => { const s = new Set(prev); s.delete(blogId); return s; });
    } else {
      await supabase.from('blog_likes').insert({ blog_id: blogId, user_id: user.id });
      setLikedIds(prev => new Set([...prev, blogId]));
    }
  };

  const toggleBookmark = async (blogId: string, e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) { router.push('/login'); return; }
    const supabase = createClient();
    const isBookmarked = bookmarkedIds.has(blogId);
    if (isBookmarked) {
      await supabase.from('blog_bookmarks').delete().eq('blog_id', blogId).eq('user_id', user.id);
      setBookmarkedIds(prev => { const s = new Set(prev); s.delete(blogId); return s; });
    } else {
      await supabase.from('blog_bookmarks').insert({ blog_id: blogId, user_id: user.id });
      setBookmarkedIds(prev => new Set([...prev, blogId]));
    }
  };

  const followAuthor = async (authorId: string) => {
    if (!user) { router.push('/login'); return; }
    const supabase = createClient();
    await supabase.from('blog_author_follows').insert({ follower_id: user.id, author_id: authorId });
    setFollowedIds(prev => new Set([...prev, authorId]));
    // refresh following data
    fetchFollowing();
  };

  const unfollowAuthor = async (authorId: string) => {
    if (!user) return;
    const supabase = createClient();
    await supabase.from('blog_author_follows').delete().eq('follower_id', user.id).eq('author_id', authorId);
    setFollowedIds(prev => { const s = new Set(prev); s.delete(authorId); return s; });
    setFollowedAuthors(prev => prev.filter(a => a.id !== authorId));
    setFollowingBlogs(prev => prev.filter(b => b.author_id !== authorId));
  };

  const FEED_TABS: { id: FeedTab; label: string; icon: React.ReactNode }[] = [
    { id: 'all', label: 'All', icon: <PencilSquareIcon style={{ width: 14, height: 14 }} /> },
    { id: 'trending', label: 'Trending', icon: <TrophyIcon style={{ width: 14, height: 14 }} /> },
    { id: 'recommended', label: 'Recommended', icon: <SparklesIcon style={{ width: 14, height: 14 }} /> },
    { id: 'following', label: 'Following', icon: <RssIcon style={{ width: 14, height: 14 }} />, },
  ];

  return (
    <div className="min-h-screen bg-background">
      {loadError && <div role="alert" className="m-4 rounded-xl border border-red-500/30 p-4 text-sm text-red-500">{loadError} <button onClick={fetchBlogs} className="ml-2 font-semibold underline">Retry</button></div>}
      {/* ─── Hero Header ─── */}
      <div className="relative overflow-hidden border-b border-border/40"
        style={{ background: 'linear-gradient(135deg, rgba(59,110,245,0.12) 0%, rgba(139,92,246,0.08) 50%, rgba(6,182,212,0.06) 100%)' }}>
        <div className="absolute inset-0 opacity-30"
          style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(59,110,245,0.15) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139,92,246,0.1) 0%, transparent 50%)' }} />
        <div className="relative max-w-5xl mx-auto px-4 py-10 lg:py-14">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <PencilSquareIcon className="text-primary" style={{ width: 18, height: 18 }} />
                </div>
                <span className="text-xs font-bold text-primary uppercase tracking-widest">Community Blogs</span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-foreground leading-tight mb-2">
                Ideas worth sharing
              </h1>
              <p className="text-muted-foreground text-sm lg:text-base max-w-md">
                Insights, guides, and stories from our community of traders, researchers, and crypto enthusiasts.
              </p>
            </div>
            {user && (
              <Link
                href="/blogs/write"
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-2xl text-sm font-bold text-white transition-all duration-200 hover:scale-105 flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #3b6ef5 0%, #6d28d9 100%)', boxShadow: '0 4px 20px rgba(59,110,245,0.35)' }}
              >
                <PencilSquareIcon style={{ width: 18, height: 18 }} />
                Write a Blog
              </Link>
            )}
          </div>

          {/* Search */}
          <div className="mt-6 relative max-w-lg">
            <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" style={{ width: 16, height: 16 }} />
            <input
              type="text"
              placeholder="Search blogs..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card/80 border border-border/60 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 transition-all"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* ─── Feed Tabs ─── */}
        <div className="grid grid-cols-4 gap-1 p-1 rounded-2xl bg-card border border-border/40 mb-6">
          {FEED_TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setFeedTab(tab.id)}
              className={`flex items-center justify-center gap-1 py-2 rounded-xl text-xs font-semibold transition-all duration-200 min-w-0 ${
                feedTab === tab.id
                  ? 'bg-primary text-white shadow-md shadow-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <span className="flex-shrink-0">{tab.icon}</span>
              <span className="truncate">{tab.label}</span>
              {tab.id === 'following' && followedIds.size > 0 && (
                <span className={`flex-shrink-0 px-1 py-0.5 rounded-full text-[9px] font-bold ${feedTab === 'following' ? 'bg-white/20 text-white' : 'bg-primary/15 text-primary'}`}>
                  {followedIds.size}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ─── Following Tab ─── */}
        {feedTab === 'following' && (
          <FollowingFeed
            blogs={followingBlogs}
            followedAuthors={followedAuthors}
            followedIds={followedIds}
            onUnfollow={unfollowAuthor}
            loading={followingLoading}
            user={user}
            onLogin={() => router.push('/login')}
            likedIds={likedIds}
            bookmarkedIds={bookmarkedIds}
            onToggleLike={toggleLike}
            onToggleBookmark={toggleBookmark}
          />
        )}

        {/* ─── Trending Tab ─── */}
        {feedTab === 'trending' && (
          <div>
            {trendingBlogs.length === 0 ? (
              <div className="text-center py-16">
                <TrophyIcon className="text-muted-foreground mx-auto mb-3" style={{ width: 32, height: 32 }} />
                <p className="text-foreground font-semibold">No trending blogs yet</p>
              </div>
            ) : (
              <TrendingSection blogs={trendingBlogs} />
            )}
          </div>
        )}

        {/* ─── Recommended Tab ─── */}
        {feedTab === 'recommended' && (
          <div>
            {recommendedBlogs.length === 0 ? (
              <div className="text-center py-16">
                <SparklesIcon className="text-muted-foreground mx-auto mb-3" style={{ width: 32, height: 32 }} />
                <p className="text-foreground font-semibold">No recommendations yet</p>
                <p className="text-muted-foreground text-sm mt-1">Read more blogs to get personalised picks.</p>
              </div>
            ) : (
              <RecommendedCarousel blogs={recommendedBlogs} />
            )}
          </div>
        )}

        {/* ─── All Tab ─── */}
        {feedTab === 'all' && (
          <>
            {/* Tag filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none mb-8">
              {ALL_TAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => setActiveTag(tag)}
                  className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 ${
                    activeTag === tag
                      ? 'bg-primary text-white border-primary shadow-md shadow-primary/20'
                      : 'bg-card border-border/60 text-muted-foreground hover:text-foreground hover:border-primary/40'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="space-y-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="rounded-2xl bg-card border border-border/40 p-6 animate-pulse">
                    <div className="h-5 bg-muted rounded w-3/4 mb-3" />
                    <div className="h-3 bg-muted rounded w-full mb-2" />
                    <div className="h-3 bg-muted rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* Featured post */}
                {featured && (
                  <Link href={`/blogs/${featured.slug}`} className="block mb-8 group">
                    <div className="rounded-2xl border border-border/40 bg-card overflow-hidden transition-all duration-300 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5"
                      style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
                      {featured.cover_image_url && (
                        <div className="relative h-56 lg:h-72 overflow-hidden">
                          <img src={featured.cover_image_url} alt={featured.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          <div className="absolute inset-0 bg-gradient-to-t from-card/90 via-card/20 to-transparent" />
                          <div className="absolute top-4 left-4">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-primary text-white shadow-lg">
                              <FireIcon style={{ width: 11, height: 11 }} /> Featured
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="p-6">
                        {!featured.cover_image_url && (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold bg-primary/15 text-primary border border-primary/20 mb-3">
                            <FireIcon style={{ width: 11, height: 11 }} /> Featured
                          </span>
                        )}
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {(featured.tags || []).slice(0, 3).map(tag => (
                            <span key={tag} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TAG_COLORS[tag] || 'bg-muted text-muted-foreground border-border'}`}>{tag}</span>
                          ))}
                        </div>
                        <h2 className="text-xl lg:text-2xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
                          {featured.title}
                        </h2>
                        {featured.excerpt && (
                          <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2 mb-4">{featured.excerpt}</p>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <AuthorAvatar
                              name={(featured.user_profiles as any)?.full_name || 'Author'}
                              avatarUrl={(featured.user_profiles as any)?.avatar_url}
                              size={8}
                            />
                            <div>
                              <p className="text-xs font-semibold text-foreground">{(featured.user_profiles as any)?.full_name || 'Community Member'}</p>
                              <p className="text-[10px] text-muted-foreground">{timeAgo(featured.published_at || featured.created_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-muted-foreground">
                            <span className="flex items-center gap-1 text-xs">
                              <ClockIcon style={{ width: 13, height: 13 }} /> {featured.read_time_mins}m
                            </span>
                            <span className="flex items-center gap-1 text-xs">
                              <HeartIcon style={{ width: 13, height: 13 }} /> {featured.likes_count}
                            </span>
                            <span className="flex items-center gap-1 text-xs">
                              <ChatBubbleLeftIcon style={{ width: 13, height: 13 }} /> {featured.comments_count}
                            </span>
                            <span className="flex items-center gap-1.5 text-xs font-semibold text-primary group-hover:gap-2.5 transition-all">
                              Read <ArrowRightIcon style={{ width: 12, height: 12 }} />
                            </span>
                          </div>
                        </div>
                        {/* Follow author button on featured */}
                        {user && featured.author_id !== user.id && (
                          <div className="mt-4 pt-4 border-t border-border/40">
                            <button
                              onClick={e => { e.preventDefault(); followedIds.has(featured.author_id) ? unfollowAuthor(featured.author_id) : followAuthor(featured.author_id); }}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                followedIds.has(featured.author_id)
                                  ? 'border-border/60 text-muted-foreground hover:border-red-400/50 hover:text-red-400'
                                  : 'border-primary/40 text-primary bg-primary/5 hover:bg-primary/10'
                              }`}
                            >
                              {followedIds.has(featured.author_id)
                                ? <><UserMinusIcon style={{ width: 13, height: 13 }} /> Following</>
                                : <><UserPlusIcon style={{ width: 13, height: 13 }} /> Follow {(featured.user_profiles as any)?.full_name?.split(' ')[0] || 'Author'}</>
                              }
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                )}

                {/* Blog grid */}
                {blogs.length === 0 && !featured ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
                      <PencilSquareIcon className="text-muted-foreground" style={{ width: 28, height: 28 }} />
                    </div>
                    <p className="text-foreground font-semibold mb-1">No blogs yet</p>
                    <p className="text-muted-foreground text-sm mb-6">Be the first to share your insights with the community.</p>
                    {user && (
                      <Link href="/blogs/write" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
                        <PencilSquareIcon style={{ width: 16, height: 16 }} /> Write First Blog
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {blogs.map(blog => (
                      <Link key={blog.id} href={`/blogs/${blog.slug}`} className="group block">
                        <article className="h-full rounded-2xl border border-border/40 bg-card overflow-hidden transition-all duration-300 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5">
                          {blog.cover_image_url && (
                            <div className="h-40 overflow-hidden">
                              <img src={blog.cover_image_url} alt={blog.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            </div>
                          )}
                          <div className="p-5">
                            <div className="flex flex-wrap gap-1.5 mb-2.5">
                              {(blog.tags || []).slice(0, 2).map(tag => (
                                <span key={tag} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${TAG_COLORS[tag] || 'bg-muted text-muted-foreground border-border'}`}>{tag}</span>
                              ))}
                            </div>
                            <h3 className="text-base font-bold text-foreground mb-1.5 group-hover:text-primary transition-colors leading-snug line-clamp-2">
                              {blog.title}
                            </h3>
                            {blog.excerpt && (
                              <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2 mb-4">{blog.excerpt}</p>
                            )}
                            <div className="flex items-center justify-between mt-auto pt-3 border-t border-border/40">
                              <div className="flex items-center gap-2">
                                <AuthorAvatar
                                  name={(blog.user_profiles as any)?.full_name || 'Author'}
                                  avatarUrl={(blog.user_profiles as any)?.avatar_url}
                                  size={6}
                                />
                                <div>
                                  <p className="text-[11px] font-semibold text-foreground leading-none">{(blog.user_profiles as any)?.full_name || 'Member'}</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(blog.published_at || blog.created_at)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {user && blog.author_id !== user.id && (
                                  <button
                                    onClick={e => { e.preventDefault(); followedIds.has(blog.author_id) ? unfollowAuthor(blog.author_id) : followAuthor(blog.author_id); }}
                                    className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold border transition-all ${
                                      followedIds.has(blog.author_id)
                                        ? 'border-border/60 text-muted-foreground hover:border-red-400/50 hover:text-red-400'
                                        : 'border-primary/40 text-primary bg-primary/5 hover:bg-primary/10'
                                    }`}
                                  >
                                    {followedIds.has(blog.author_id)
                                      ? <><UserMinusIcon style={{ width: 10, height: 10 }} /> Following</>
                                      : <><UserPlusIcon style={{ width: 10, height: 10 }} /> Follow</>
                                    }
                                  </button>
                                )}
                                <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                                  <ClockIcon style={{ width: 11, height: 11 }} /> {blog.read_time_mins}m
                                </span>
                                <button
                                  onClick={e => toggleLike(blog.id, e)}
                                  className={`flex items-center gap-1 text-[11px] transition-colors ${likedIds.has(blog.id) ? 'text-red-400' : 'text-muted-foreground hover:text-red-400'}`}
                                >
                                  {likedIds.has(blog.id)
                                    ? <HeartSolid style={{ width: 13, height: 13 }} />
                                    : <HeartIcon style={{ width: 13, height: 13 }} />}
                                  {blog.likes_count + (likedIds.has(blog.id) ? 1 : 0)}
                                </button>
                                <button
                                  onClick={e => toggleBookmark(blog.id, e)}
                                  className={`transition-colors ${bookmarkedIds.has(blog.id) ? 'text-amber-400' : 'text-muted-foreground hover:text-amber-400'}`}
                                >
                                  {bookmarkedIds.has(blog.id)
                                    ? <BookmarkSolid style={{ width: 13, height: 13 }} />
                                    : <BookmarkIcon style={{ width: 13, height: 13 }} />}
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      </Link>
                    ))}
                  </div>
                )}

                {/* Write CTA for non-logged-in */}
                {!user && (
                  <div className="mt-12 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-8 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-4">
                      <SparklesIcon className="text-primary" style={{ width: 24, height: 24 }} />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-2">Share your knowledge</h3>
                    <p className="text-muted-foreground text-sm mb-5 max-w-sm mx-auto">
                      Join our community and publish your insights. Your blog goes live after a quick admin review.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      <Link href="/signup" className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors">
                        Get Started
                      </Link>
                      <Link href="/login" className="px-5 py-2.5 rounded-xl border border-border text-sm font-semibold text-foreground hover:bg-muted transition-colors">
                        Sign In
                      </Link>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
